import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
  Tooltip,
  ButtonBase
} from '@mui/material';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import AddIcon from '@mui/icons-material/Add';
import { collapsedSidebarControlSx } from '../../components/sidebarStyles';
import { PATH_DASHBOARD, PATH_ORG } from '../../routes/paths';
import useLocales from '../../hooks/useLocales';
import { useSelector } from '../../redux/store';
import type { RootState } from '../../redux/store';
import { AddTopicDialog } from './AddTopicDialog';

type CreatePopoverProps = {
  collapsed?: boolean;
};

export default function CreatePopover({ collapsed = false }: CreatePopoverProps) {
  const { translate } = useLocales();
  const navigate = useNavigate();
  const activeOrgGuid = useSelector((s: RootState) => s.organizationContext.activeOrgGuid);
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const [addTopicOpen, setAddTopicOpen] = useState(false);
  const open = Boolean(anchor);

  const handleClose = () => setAnchor(null);

  return (
    <>
      <Tooltip title={collapsed ? translate('overview.create') : ''} placement="right">
        <ButtonBase
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label={translate('overview.create')}
          aria-haspopup="menu"
          aria-expanded={open}
          sx={{
            width: collapsed ? collapsedSidebarControlSx.width : '100%',
            minWidth: collapsed ? collapsedSidebarControlSx.width : 0,
            height: collapsed ? collapsedSidebarControlSx.height : 36,
            mx: collapsed ? 'auto' : 0,
            px: collapsed ? 0 : 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            gap: 0,
            borderRadius: collapsedSidebarControlSx.borderRadius,
            color: 'text.primary',
            backgroundColor: collapsed ? 'transparent' : 'action.hover',
            '&:hover': {
              backgroundColor: collapsed ? 'action.hover' : 'action.selected'
            },
            '&.Mui-focusVisible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2
            }
          }}
        >
          <ListItemIcon
            sx={{
              width: 20,
              minWidth: collapsed ? 20 : 28,
              mr: collapsed ? 0 : 2,
              height: 20,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'text.secondary'
            }}
          >
            <AddIcon sx={{ width: '100%', height: '100%' }} />
          </ListItemIcon>
          {!collapsed && (
            <Typography variant="body2" sx={{ fontWeight: 'fontWeightRegular' }}>
              {translate('overview.create')}
            </Typography>
          )}
        </ButtonBase>
      </Tooltip>

      <Menu
        anchorEl={anchor}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          list: { sx: { py: 0.5 } },
          paper: {
            sx: {
              mt: 1,
              minWidth: 220,
              boxShadow: (theme) => theme.customShadows?.z8 ?? theme.shadows[2],
              '& .MuiMenuItem-root': { px: 1.5, py: 0.5, gap: 1, minHeight: 48 },
              '& .MuiMenuItem-root .MuiListItemIcon-root': {
                minWidth: 20,
                color: 'text.secondary'
              },
              '& .MuiListItemText-root': { my: 0 }
            }
          }
        }}
      >
        {!activeOrgGuid && (
          <>
            <MenuItem
              onClick={() => {
                handleClose();
                setAddTopicOpen(true);
              }}
            >
              <ListItemIcon>
                <ArticleOutlinedIcon sx={{ fontSize: 20 }} />
              </ListItemIcon>
              <ListItemText
                primary={translate('overview.create_topic_label')}
                secondary={translate('overview.add_topic_subtitle')}
                slotProps={{
                  primary: {
                    variant: 'body2',
                    sx: { fontWeight: (theme) => theme.typography.fontWeightMedium }
                  },
                  secondary: { variant: 'caption' }
                }}
              />
            </MenuItem>
          </>
        )}

        <MenuItem
          onClick={() => {
            handleClose();
            navigate(activeOrgGuid ? PATH_ORG.pathwaysCreate : PATH_DASHBOARD.pathway.new);
          }}
        >
          <ListItemIcon>
            <AccountTreeOutlinedIcon sx={{ fontSize: 20 }} />
          </ListItemIcon>
          <ListItemText
            primary={translate('overview.create_pathway_label')}
            secondary={translate('overview.new_pathway_subtitle')}
            slotProps={{
              primary: {
                variant: 'body2',
                sx: { fontWeight: (theme) => theme.typography.fontWeightMedium }
              },
              secondary: { variant: 'caption' }
            }}
          />
        </MenuItem>
      </Menu>
      <AddTopicDialog open={addTopicOpen} onClose={() => setAddTopicOpen(false)} />
    </>
  );
}
